/**
 * Stage 2 decision set — the checklist a site gets when early screening is
 * complete: every mature-validation decision with its status, the stances of
 * the options it compares, the site facts behind it, the open questions the
 * Stage 1 reports raised, and any one-click proposals.
 *
 * Pure and deterministic: the caller supplies "today" (`asOf`), the grid/gas
 * availability resolution and the study-area acreage, so the same inputs
 * always produce the same set. Nothing here writes; proposals are applied by
 * `applyStage2Choice` on an explicit user action.
 */
import type { SiteEntity } from '../../types/site';
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { PerSourceAvailabilityResolution } from '../powerAvailability/resolvePerSourceAvailability';
import { effectivePhaseCapacity, formatPhasePowerSource } from '../projectIntent';
import { formatWaterSources } from '../intent/waterSource';
import { INTERCONNECTION_STATUS_LABELS } from '../reportPreferences/labels';
import { assessCoolingOptions, screeningWaterRisks } from './coolingStances';
import { assessPowerOptions, isOzoneTransportReason, optionMissesPhaseDate } from './powerStances';
import {
  STAGE2_DECISIONS,
  resolveStage2DecisionStatus,
  stage2FocusField,
  type Stage2DecisionDefinition,
} from './stage2Catalogue';
import {
  latestStage1Report,
  mentionsBattery,
  phaseOrdinal,
  powerOptionsNamedIn,
  type PowerOptionId,
} from './stage2Evidence';
import type { SiteDecisionStageStatus } from './siteDecisionStage';
import {
  stage2BasisChanged,
  stage2BasisKey,
  stage2LegacyBasisKey,
  type SedSectionHasher,
} from './stage2Basis';
import {
  openQuestionFromClaim,
  type BtmStrategy,
  type Stage2Decision,
  type Stage2DecisionId,
  type Stage2DecisionReview,
  type Stage2DecisionSet,
  type Stage2Fact,
  type Stage2OpenQuestion,
  type Stage2OptionAssessment,
  type Stage2Proposal,
} from './stage2Types';

export interface DeriveStage2Inputs {
  site: Pick<SiteEntity, 'id' | 'projectIntent' | 'typedPreferencesDefaults' | 'stage2DecisionReview'>;
  sed: SiteEvidenceDocument | null | undefined;
  /** The site's saved reports; the latest Stage 1 report of each type is read. */
  reports: readonly SiteRagReport[];
  stage: SiteDecisionStageStatus;
  availability?: PerSourceAvailabilityResolution | null;
  studyAreaAcres?: number | null;
  /** Today as an ISO date, supplied by the caller. */
  asOf: string;
  /**
   * SED section content hasher (`computeSedSectionContentHashesV2` in
   * production), which keys the evidence basis on the content of the sections
   * Stage 2 reads.
   */
  hashSedSections: SedSectionHasher;
}

/** Display label for each grid vs on-site strategy (the Intent tab's vocabulary). */
export const STRATEGY_LABEL: Readonly<Record<BtmStrategy, string>> = {
  'grid-only': 'Grid only',
  hybrid: 'Hybrid (grid + on-site generation)',
  'btm-only': 'Behind-the-meter only',
  islanded: 'Islanded (off-grid)',
};

function valueOrNotSet(value: string | number | null | undefined): string {
  if (value == null || value === '' || value === 'undecided' || value === 'unknown') return 'Not set';
  return String(value);
}

function factsFor(
  id: Stage2DecisionId,
  intent: ProjectIntent | null | undefined,
  sed: SiteEvidenceDocument | null | undefined,
  strategy: BtmStrategy | undefined,
  feasibility: SiteRagReport | null,
): Stage2Fact[] {
  const phases = [...(intent?.phases ?? [])].sort((a, b) => a.sequence - b.sequence);
  switch (id) {
    case 'power-supply':
      return [
        { label: 'Power strategy', value: strategy ? STRATEGY_LABEL[strategy] : 'Not declared', evidencePath: "typedPreferencesDefaults['power-availability'].btmStrategy" },
        ...phases.map(phase => ({ label: phase.label || `Phase ${phase.sequence}`, value: formatPhasePowerSource(phase) || 'Undecided' })),
      ];
    case 'cooling': {
      const water = sed?.waterStress?.data;
      return [
        { label: 'Rack cooling', value: valueOrNotSet(intent?.coolingStrategy) },
        { label: 'Heat rejection', value: valueOrNotSet(intent?.bopHeatRejection) },
        ...(water ? [{ label: 'Baseline water stress', value: water.bwsLabel || water.stressLevel, evidencePath: 'sed.waterStress' }] : []),
      ];
    }
    case 'build-phases': {
      if (phases.length === 0) {
        return [{ label: 'Campus in-service date', value: intent?.inServiceDate?.trim() || 'No exact date' }];
      }
      return phases.map(phase => ({
        label: phase.label || `Phase ${phase.sequence}`,
        value: `${effectivePhaseCapacity(phase).displayLabel} cumulative · ${phase.targetInServiceDate?.trim() || 'no exact date'}`,
      }));
    }
    case 'design-basis':
      return [
        { label: 'PUE', value: valueOrNotSet(intent?.pueValue) },
        { label: 'Power factor', value: valueOrNotSet(intent?.powerFactor) },
        { label: 'Tier target', value: valueOrNotSet(intent?.datacenterTierTarget) },
        { label: 'Redundancy', value: valueOrNotSet(intent?.redundancyClass) },
      ];
    case 'firm-capacity': {
      const pathwayPath = feasibility ? `report:${feasibility.id}.developmentPathway` : undefined;
      return [
        { label: 'Firm-capacity target', value: valueOrNotSet(intent?.capacityCreditIntent?.target) },
        ...(feasibility?.developmentPathway ?? [])
          .filter(step => typeof step.firmCapacityVerdict === 'string' && step.firmCapacityVerdict.trim())
          .map(step => ({
            label: `Screening ${step.phase}`,
            value: `firm-capacity check: ${step.firmCapacityVerdict}`,
            evidencePath: pathwayPath,
          })),
      ];
    }
    case 'water-supply': {
      const water = sed?.waterStress?.data;
      const sources = formatWaterSources(intent?.waterSource);
      return [
        { label: 'Declared sources', value: sources || 'Not set' },
        { label: 'Water rights', value: valueOrNotSet(intent?.waterSource?.rightsStatus) },
        ...(water ? [{ label: 'Baseline water stress', value: water.bwsLabel || water.stressLevel, evidencePath: 'sed.waterStress' }] : []),
      ];
    }
    case 'grid-path': {
      const grid = sed?.grid?.data;
      return [
        { label: intent?.interconnectionStatus?.queueStatus === 'executed-load-service' ? 'Utility service status' : 'Queue status',
          value: intent?.interconnectionStatus?.queueStatus === 'executed-load-service'
            ? `${INTERCONNECTION_STATUS_LABELS['executed-load-service']}; delivery and commissioning remain separate`
            : valueOrNotSet(intent?.interconnectionStatus?.queueStatus) },
        ...(intent?.interconnectionStatus?.queueStatus === 'executed-load-service'
          ? [
              ...(intent.interconnectionStatus.queueId ? [{ label: 'Utility application ID', value: intent.interconnectionStatus.queueId }] : []),
              ...(intent.interconnectionStatus.studyPhase ? [{ label: 'Study context', value: intent.interconnectionStatus.studyPhase }] : []),
            ]
          : []),
        ...(grid?.substationName
          ? [{ label: 'Nearest substation', value: `${grid.substationName} · ${grid.substationDistKm.toFixed(1)} km · ${grid.voltage}`, evidencePath: 'sed.grid' }]
          : []),
        ...(grid?.utilityName ? [{ label: 'Utility', value: grid.utilityName, evidencePath: 'sed.grid.utilityName' }] : []),
      ];
    }
    case 'fuel-supply': {
      const fuel = sed?.fuelSupply?.data;
      return [
        { label: 'Declared fuel access', value: valueOrNotSet(intent?.fuelAccess?.supply) },
        ...(fuel?.nearestPipelineKm != null
          ? [{ label: 'Nearest mapped pipeline', value: `${fuel.nearestPipelineKm.toFixed(1)} km${fuel.nearestPipelineOperator ? ` · ${fuel.nearestPipelineOperator}` : ''}`, evidencePath: 'sed.fuelSupply' }]
          : []),
      ];
    }
    case 'nuclear': {
      const nuclear = sed?.nuclearInfra?.data;
      return [
        { label: 'Nuclear interest', value: valueOrNotSet(intent?.nuclearInterest) },
        { label: 'Reactor design class', value: valueOrNotSet(intent?.reactorDesignClass) },
        ...(nuclear?.nearestReactorName
          ? [{
              label: 'Nearest operating reactor',
              value:
                nuclear.nearestReactorDistKm >= 900 || /^none\b/i.test(nuclear.nearestReactorName.trim())
                  ? nuclear.nearestReactorName
                  : `${nuclear.nearestReactorName} · ${Math.round(nuclear.nearestReactorDistKm)} km`,
              evidencePath: 'sed.nuclearInfra',
            }]
          : []),
      ];
    }
    case 'commercial':
      return [
        { label: 'Offtake', value: valueOrNotSet(intent?.offtake?.structure) },
        { label: 'Carbon commitment', value: valueOrNotSet(intent?.carbonCommitment) },
      ];
    default: {
      const exhaustive: never = id;
      void exhaustive;
      return [];
    }
  }
}

/**
 * The grid vs on-site strategy the power stances support, or null. Exported so
 * the Stakeholder Brief states the same recommendation the Design view offers.
 */
export function strategyProposal(
  options: readonly Stage2OptionAssessment[],
  feasibility: SiteRagReport | null,
): Stage2Proposal | null {
  const grid = options.find(option => option.optionId === 'grid');
  const documented = grid?.reasons.find(reason => reason.evidencePath === 'sed.grid.capacityDetail');
  if (documented?.effect === 'favoured') {
    return { kind: 'btm-strategy', value: 'grid-only', summary: 'Grid only — documented utility capacity covers the declared load.', reasons: [documented] };
  }
  if (documented?.effect === 'constrained') {
    return { kind: 'btm-strategy', value: 'hybrid', summary: 'Hybrid — documented utility capacity covers only part of the declared load.', reasons: [documented] };
  }
  const named = (feasibility?.developmentPathway ?? []).map(step => powerOptionsNamedIn(step.powerSource));
  const namesGrid = named.some(list => list.includes('grid'));
  const namesOnSite = named.some(list => list.some(option => option !== 'grid'));
  if (namesGrid && namesOnSite) {
    return {
      kind: 'btm-strategy',
      value: 'hybrid',
      summary: 'Hybrid — the screening pathway combines grid import with on-site generation.',
      reasons: [{ text: 'The screening pathway names both grid import and on-site generation.', basis: 'screening-report', effect: 'viable', evidencePath: feasibility ? `report:${feasibility.id}.developmentPathway` : undefined }],
    };
  }
  return null;
}

/**
 * Per-phase supply options the screening pathway names and the evidence does
 * not constrain. A phase without grid import is proposed 'islanded' only when
 * the owner declared that phase islanded; a behind-the-meter-only strategy is
 * a meter arrangement, not an islanding declaration (BTM generation can run in
 * parallel with the grid), so `_strategy` no longer sets the connection.
 */
export function phasePowerProposal(
  intent: ProjectIntent | null | undefined,
  options: readonly Stage2OptionAssessment[],
  feasibility: SiteRagReport | null,
  _strategy: BtmStrategy | undefined,
  asOf: string,
): Stage2Proposal | null {
  const pathway = feasibility?.developmentPathway ?? [];
  if (!intent || pathway.length === 0) return null;
  const stanceOf = new Map(options.map(option => [option.optionId, option.stance]));
  const phases = [...(intent.phases ?? [])].sort((a, b) => a.sequence - b.sequence);
  const proposed: Extract<Stage2Proposal, { kind: 'phase-power' }>['phases'] = [];
  for (const phase of phases) {
    const hasTechnology = (phase.powerSourceConfig?.components.length ?? 0) > 0
      || (phase.powerSource != null && phase.powerSource !== 'undecided');
    if (hasTechnology) continue;
    const step = pathway.find(entry => phaseOrdinal(entry.phase) === phase.sequence);
    if (!step) continue;
    const date = phase.targetInServiceDate?.trim();
    const usable = powerOptionsNamedIn(step.powerSource).filter((option: PowerOptionId) => {
      const stance = stanceOf.get(option);
      if (stance === 'constrained' || stance === 'excluded-by-evidence' || stance === 'not-assessed') return false;
      return !(date && optionMissesPhaseDate(option, date, asOf));
    });
    if (usable.length === 0) continue;
    proposed.push({
      phaseId: phase.id,
      phaseLabel: phase.label || `Phase ${phase.sequence}`,
      options: usable,
      battery: mentionsBattery(step.powerSource),
      gridConnection: usable.includes('grid')
        ? 'grid-connected'
        : _strategy === 'islanded' || phase.powerSourceConfig?.gridConnection === 'islanded' ? 'islanded' : 'undecided',
    });
  }
  if (proposed.length === 0) return null;
  return {
    kind: 'phase-power',
    phases: proposed,
    summary: proposed
      .map(entry => `${entry.phaseLabel}: ${entry.options.map(option => options.find(o => o.optionId === option)?.label ?? option).join(' + ')}${entry.battery ? ' + storage' : ''}`)
      .join('; '),
    reasons: [{ text: 'Named by the screening pathway and not constrained by the site evidence for these phases.', basis: 'screening-report', effect: 'viable', evidencePath: feasibility ? `report:${feasibility.id}.developmentPathway` : undefined }],
  };
}

/** The single favoured cold end and rack loop for the open cooling fields, or null. */
export function coolingProposal(
  intent: ProjectIntent | null | undefined,
  options: readonly Stage2OptionAssessment[],
): Stage2Proposal | null {
  const favoured = (prefix: string) => {
    const matches = options.filter(option => option.optionId.startsWith(prefix) && option.stance === 'favoured');
    return matches.length === 1 ? matches[0]! : null;
  };
  const sinkOpen = intent?.bopHeatRejection == null || intent.bopHeatRejection === 'unknown';
  const loopOpen = intent?.coolingStrategy == null || intent.coolingStrategy === 'undecided';
  const sink = sinkOpen ? favoured('sink:') : null;
  const loop = loopOpen ? favoured('loop:') : null;
  if (!sink && !loop) return null;
  return {
    kind: 'cooling',
    ...(sink ? { bopHeatRejection: sink.optionId.slice('sink:'.length) as 'dry-cooler' | 'wet-cooling-tower' | 'hybrid' } : {}),
    ...(loop ? { coolingStrategy: loop.optionId.slice('loop:'.length) as 'air' | 'liquid' | 'hybrid' } : {}),
    summary: [sink?.label, loop?.label].filter(Boolean).join(' · '),
    reasons: [...(sink?.reasons ?? []), ...(loop?.reasons ?? [])].filter(reason => reason.effect === 'favoured'),
  };
}

function openQuestionsFor(
  id: Stage2DecisionId,
  status: Stage2Decision['status'],
  options: readonly Stage2OptionAssessment[],
  strategy: BtmStrategy | undefined,
  feasibility: SiteRagReport | null,
): Stage2OpenQuestion[] {
  const questions: Stage2OpenQuestion[] = [];
  if (id === 'water-supply' && feasibility) {
    for (const risk of screeningWaterRisks(feasibility)) {
      const question = openQuestionFromClaim(risk, 'feasibility', feasibility.id);
      if (question) questions.push(question);
    }
  }
  if (id === 'power-supply' && strategy !== 'grid-only') {
    const gas = options.find(option => option.optionId === 'gas-cc');
    // Only designation evidence answers the question; the Ozone Transport
    // Region reason rests on location and leaves it open.
    if (gas && !gas.reasons.some(reason => reason.basis === 'regulation' && !isOzoneTransportReason(reason))) {
      questions.push({ question: "Confirm the county's current EPA Green Book ozone and PM2.5 designations before siting combustion units.", followUp: 'EPA Green Book (NAYRO county export).' });
    }
    const grid = options.find(option => option.optionId === 'grid');
    if (grid && !grid.reasons.some(reason => reason.evidencePath === 'sed.grid.capacityDetail')) {
      questions.push({ question: 'Request a utility capacity or will-serve letter with the deliverable MW and date.' });
    }
  }
  if (id === 'build-phases' && status === 'open') {
    questions.push({ question: 'Set an exact target date for each phase: mature analyses do not infer dates from quarters or ranges.' });
  }
  return questions;
}

/** Build the Stage 2 decision set. */
export function deriveStage2Decisions(inputs: DeriveStage2Inputs): Stage2DecisionSet {
  const intent = inputs.site.projectIntent;
  const strategy = inputs.site.typedPreferencesDefaults?.['power-availability']?.btmStrategy as BtmStrategy | undefined;
  const review: Stage2DecisionReview | null = inputs.site.stage2DecisionReview ?? null;
  const feasibility = latestStage1Report(inputs.reports, 'feasibility');
  // Environmental is Stage 2 · Mature validation since the owner's 2026-09-30
  // move, so the decisions usually open before it exists: the air rule then
  // reads the SED's EPA listing and research text alone. A report that arrives
  // later is new evidence and moves the basis key like any other.
  const environmental = latestStage1Report(inputs.reports, 'environmental');
  const basisKey = stage2BasisKey(
    { sed: inputs.sed, feasibility, environmental, studyAreaAcres: inputs.studyAreaAcres ?? null },
    inputs.hashSedSections,
  );
  // Acceptances saved before content-based keys are compared in their own format.
  const legacyKey = stage2LegacyBasisKey(
    inputs.sed,
    [feasibility, environmental, latestStage1Report(inputs.reports, 'public-sentiment')],
  );
  // No Stage 2 rule reads Public Sentiment, so its report is not part of the basis.
  const basisReportIds = [feasibility, environmental]
    .filter((report): report is SiteRagReport => report != null)
    .map(report => report.id);
  const available = inputs.stage.stage1Complete && inputs.stage.screeningOutcome !== 'declined';

  const powerOptions = assessPowerOptions({
    intent,
    sed: inputs.sed,
    availability: inputs.availability ?? null,
    feasibility,
    environmental,
    studyAreaAcres: inputs.studyAreaAcres ?? null,
    asOf: inputs.asOf,
  });
  const coolingOptions = assessCoolingOptions({ intent, sed: inputs.sed, feasibility });

  const decisions: Stage2Decision[] = STAGE2_DECISIONS.map((definition: Stage2DecisionDefinition) => {
    const status = resolveStage2DecisionStatus(definition.id, { intent, btmStrategy: strategy, review });
    const options = definition.id === 'power-supply'
      ? powerOptions
      : definition.id === 'cooling' ? coolingOptions : [];
    const proposals: Stage2Proposal[] = [];
    if (status === 'open' || status === 'deferred') {
      if (definition.id === 'power-supply') {
        const strategyChoice = strategy ? null : strategyProposal(powerOptions, feasibility);
        if (strategyChoice) proposals.push(strategyChoice);
        const phaseChoice = phasePowerProposal(intent, powerOptions, feasibility, strategy ?? (strategyChoice?.kind === 'btm-strategy' ? strategyChoice.value : undefined), inputs.asOf);
        if (phaseChoice) proposals.push(phaseChoice);
      } else if (definition.id === 'cooling') {
        const choice = coolingProposal(intent, coolingOptions);
        if (choice) proposals.push(choice);
      }
    }
    const accepted = review?.accepted?.[definition.id];
    return {
      id: definition.id,
      label: definition.label,
      question: definition.question,
      status,
      scenarioShaping: definition.scenarioShaping,
      neededBy: definition.neededBy,
      focusField: stage2FocusField(definition.id),
      options,
      facts: factsFor(definition.id, intent, inputs.sed, strategy, feasibility),
      openQuestions: openQuestionsFor(definition.id, status, options, strategy, feasibility),
      proposals,
      ...(accepted && stage2BasisChanged(accepted.basisKey, { key: basisKey, legacyKey }) && (status === 'decided' || status === 'recorded-unknown')
        ? { revisit: true }
        : {}),
    };
  });

  const openScenarioShaping = decisions
    .filter(decision => decision.scenarioShaping && decision.status === 'open')
    .map(decision => decision.id);
  const promoted = !inputs.stage.scenarioPending && !inputs.stage.gridOnly;
  const headline = available && !promoted && openScenarioShaping.length > 0 ? openScenarioShaping[0]! : null;

  return {
    available,
    ...(!inputs.stage.stage1Complete
      ? { unavailableReason: 'Stage 2 decisions open once every required early-screening analysis exists.' }
      : inputs.stage.screeningOutcome === 'declined'
        ? { unavailableReason: 'Early screening declined this site. The decisions are shown for reference only.' }
        : {}),
    basisKey,
    basisReportIds,
    decisions,
    openCount: decisions.filter(decision => decision.status === 'open').length,
    openScenarioShaping,
    headline,
  };
}
